import Phaser from 'phaser';
import { Direction } from '@game/protocol';
import { DirectionState } from './direction-state.ts';
import { isTyping } from './focus.ts';

const KEY_TO_DIRECTION: Record<string, Direction> = {
  ArrowUp: Direction.North,
  ArrowRight: Direction.East,
  ArrowDown: Direction.South,
  ArrowLeft: Direction.West,
  KeyW: Direction.North,
  KeyD: Direction.East,
  KeyS: Direction.South,
  KeyA: Direction.West,
};

/**
 * Liga teclado e toque ao `DirectionState`. Só faz tradução de evento — toda a decisão de
 * qual direção vale está no `DirectionState`, que é testável sem navegador.
 *
 * O direcional de toque entra **por aqui**, e não por um caminho paralelo: o buffer de
 * input existe justamente para não perder o comando curto (PRD §6), e no celular, onde
 * todo comando é um toque de fração de segundo, é o caso em que ele mais importa.
 */
export class InputController {
  private readonly state = new DirectionState();

  constructor(scene: Phaser.Scene) {
    const keyboard = scene.input.keyboard;
    if (!keyboard) return;

    keyboard.on('keydown', (event: KeyboardEvent) => {
      const direction = KEY_TO_DIRECTION[event.code];
      if (direction === undefined) return;

      // Digitando no chat, a tecla é uma letra e não um passo. Sem esta guarda, o
      // `preventDefault` abaixo impede a inserção do caractere — e como W, A, S e D
      // aparecem em quase toda palavra do português, a frase chegava ao outro lado
      // desfigurada ("vamos descer as escadas" virava "vmo ecer ec"). As setas, pelo mesmo
      // motivo, deixavam de mover o cursor dentro do campo.
      if (isTyping(event.target)) return;

      event.preventDefault();
      this.press(direction);
    });

    // O `keyup` **não** é guardado pelo foco, e a assimetria é deliberada: quem segura uma
    // direção e clica no chat solta a tecla já com o campo focado. Ignorar esse `keyup`
    // deixaria a direção presa para sempre.
    keyboard.on('keyup', (event: KeyboardEvent) => {
      const direction = KEY_TO_DIRECTION[event.code];
      if (direction === undefined) return;

      this.release(direction);
    });

    window.addEventListener('blur', () => this.state.clear());
  }

  /** Direção acionada agora — por tecla ou por dedo no direcional. */
  press(direction: Direction): void {
    this.state.press(direction, performance.now());
  }

  release(direction: Direction): void {
    this.state.release(direction);
  }

  /**
   * Direção a executar agora. Consome o toque bufferizado, então só deve ser chamada
   * quando houver um passo disponível — ver `DirectionState.take`.
   */
  take(windowMs: number): Direction | null {
    return this.state.take(performance.now(), windowMs);
  }
}
